import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { apiRequest } from './../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import useSearchMutaion from '../../../../hooks/apis/useSearchMutaion';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { SAC_BASIC_Schema } from './SAC_BASIC_Schema';

/**
 * 산업안전보건위원회 기본정보(SAC_BASIC) 관련 React Query 훅 모음
 * - 기본 조회(useQuery): useSearch1, useSearch2, useSearch3
 *
 * 만약 MUTATION 훅에서 공통 응답 처리 외에 추가 성공 콜백이 필요하면
 * successOptions 파라미터로 onSuccess 콜백 함수를 넘겨줄 수 있다.
 */

export const useSearch1 = (options = {}) => {
  const { onSuccess } = options;
  const params = mapParamsBySchema(SAC_BASIC_Schema.mapper.search1);

  const query = useQuery({
    queryKey: ['SafetyCommittee', 'getSacBasicR1', params],
    queryFn: () =>
      apiRequest('post', Global.urls.SafetyCommittee.getSacBasicR1, params),

    enabled: true, // 자동 실행 여부
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

export const useSearch2 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SAC_BASIC_Schema.mapper.search2,
      context
    );
    return apiRequest(
      'post',
      Global.urls.SafetyCommittee.getSacBasicR2,
      encryptedParams
    );
  }, successOptions);
};

export const useSearch3 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SAC_BASIC_Schema.mapper.search3,
      context
    );
    return apiRequest(
      'post',
      Global.urls.SafetyCommittee.getSacBasicR3,
      encryptedParams
    );
  }, successOptions);
};

export const useSearch4 = (successOptions) => {
  return useSearchMutaion(
    (context) => {
      const encryptedParams = mapParamsBySchema(
        SAC_BASIC_Schema.mapper.search4,
        context
      );
      return apiRequest(
        'post',
        Global.urls.SafetyCommittee.getSacBasicR4,
        encryptedParams
      );
    },
    {
      ...successOptions,
      onSuccess: (rows) => {
        // useSearch4는 첫 번째 요소만 전달
        const firstItem = Array.isArray(rows) ? rows[0] : rows;
        successOptions?.onSuccess?.(firstItem);
      },
    }
  );
};

export const useSearch5 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SAC_BASIC_Schema.mapper.search5,
      context
    );
    return apiRequest(
      'post',
      Global.urls.SafetyCommittee.getSacBasicR5,
      encryptedParams
    );
  }, successOptions);
};

export const useSave1 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    // 서버는 단일 객체를 받으므로 배열의 첫 번째 요소만 사용
    const payloadArray = mapRowsBySchema(rows, SAC_BASIC_Schema.mapper.save1, {
      search: options.search,
      userInfo: options.userInfo,
    });
    const payload = payloadArray[0] || {};

    return apiRequest(
      'post',
      Global.urls.SafetyCommittee.saveSacBasicU1,
      payload
    );
  }, options);
};

export const useSave2 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    // 서버는 단일 객체를 받으므로 배열의 첫 번째 요소만 사용
    const payloadArray = mapRowsBySchema(rows, SAC_BASIC_Schema.mapper.save2, {
      userInfo: options.userInfo,
    });
    const payload = payloadArray[0] || {};

    return apiRequest(
      'post',
      Global.urls.SafetyCommittee.saveSacBasicU2,
      payload
    );
  }, options);
};

export const useSave3 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SAC_BASIC_Schema.mapper.save3, {
      userInfo: options.userInfo,
    });

    return apiRequest(
      'post1',
      Global.urls.SafetyCommittee.saveSacBasicU3,
      payload
    );
  }, options);
};

export const useSave4 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    // 서버는 단일 객체를 받으므로 배열의 첫 번째 요소만 사용
    const payloadArray = mapRowsBySchema(rows, SAC_BASIC_Schema.mapper.save4, {
      userInfo: options.userInfo,
    });
    const payload = payloadArray[0] || {};

    return apiRequest(
      'post',
      Global.urls.SafetyCommittee.saveSacBasicU4,
      payload
    );
  }, options);
};

export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    // 서버는 단일 객체만 받으므로 첫 번째 요소만 사용
    const payload = mapRowsBySchema(rows, SAC_BASIC_Schema.mapper.delete1);
    const singlePayload = payload[0] || {};

    return apiRequest(
      'post',
      Global.urls.SafetyCommittee.deleteSacBasicD1,
      singlePayload
    );
  }, options);
};

export const useDelete2 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SAC_BASIC_Schema.mapper.delete2);

    return apiRequest(
      'post1',
      Global.urls.SafetyCommittee.deleteSacBasicD2,
      payload
    );
  }, options);
};

export const useDelete3 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    // 서버는 단일 객체만 받으므로 첫 번째 요소만 사용
    const payload = mapRowsBySchema(rows, SAC_BASIC_Schema.mapper.delete3);
    const singlePayload = payload[0] || {};

    return apiRequest(
      'post',
      Global.urls.SafetyCommittee.deleteSacBasicD3,
      singlePayload
    );
  }, options);
};
